import { useEffect, useRef } from "react";

/*  KAIRO VOICE STINGS — 3 pre-generated noir TTS clips that fire at
    0:15, 1:30, and 3:00 after the FIRST visit's hero cinematic clears.
    One-shot per browser (localStorage). Served from /api/static_audio/.

    Audio is autoplay-triggered which modern browsers block without a
    gesture. We defer each play behind the Transmission intro dismiss
    (which IS a gesture / timeout) and silently swallow the autoplay
    rejection so first-time users on strict browsers aren't broken.

    Volume defaults to 0.65 — present but not overpowering the ambient
    design. User can mute via <audio> controls (we don't render them;
    the clips are 7-9s each).
*/
const PLAYED_KEY   = "wm_kairo_stings_played_v1";
const INTRO_KEY    = "wm_transmission_seen_v1";
const AUDIO_BASE   = `${process.env.REACT_APP_BACKEND_URL || ""}/api/static_audio`;
const SCHEDULE_MS  = [15_000, 90_000, 180_000];   // 0:15, 1:30, 3:00

export const KairoStings = () => {
  const audiosRef = useRef([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.pathname !== "/") return;
    try {
      if (localStorage.getItem(PLAYED_KEY) === "1") return;
    } catch { return; }

    let cancelled = false;
    const timers  = [];

    // Pre-load so the first play has no network stutter
    audiosRef.current = [1, 2, 3].map((slot) => {
      const a = new Audio(`${AUDIO_BASE}/kairo_0${slot}.mp3`);
      a.preload = "auto";
      a.volume  = 0.65;
      return a;
    });

    const start = () => {
      if (cancelled) return;
      // Mark played immediately so a hard refresh mid-arc won't replay.
      try { localStorage.setItem(PLAYED_KEY, "1"); } catch {}
      SCHEDULE_MS.forEach((ms, idx) => {
        timers.push(setTimeout(() => {
          if (cancelled) return;
          const a = audiosRef.current[idx];
          if (!a) return;
          const p = a.play();
          if (p && typeof p.catch === "function") p.catch(() => { /* autoplay blocked, ignore */ });
        }, ms));
      });
    };

    // Only start once the Transmission intro has cleared. If it already has,
    // start immediately. Otherwise wait up to 20s for it to clear.
    try {
      if (localStorage.getItem(INTRO_KEY) === "1") {
        start();
      } else {
        const t0 = Date.now();
        const iv = setInterval(() => {
          if (cancelled) { clearInterval(iv); return; }
          const seen = localStorage.getItem(INTRO_KEY) === "1";
          if (seen || Date.now() - t0 > 20_000) {
            clearInterval(iv);
            start();
          }
        }, 500);
        timers.push({ _cleanup: () => clearInterval(iv) });
      }
    } catch {
      start();
    }

    return () => {
      cancelled = true;
      timers.forEach((t) => {
        if (t && typeof t === "number") clearTimeout(t);
        else if (t && t._cleanup) t._cleanup();
      });
      audiosRef.current.forEach((a) => { try { a.pause(); } catch {} });
    };
  }, []);

  // Render nothing — this is a side-effect component.
  return null;
};

export default KairoStings;
